import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase, clearSupabaseAuthTokens } from '../lib/supabase';
import { logUserEvent } from '../lib/audit';
import { trackEvent } from '../lib/analytics';
import { markFreshLogin } from '../hooks/usePinLock';

export type UserRole = 'customer' | 'master_admin' | 'admin' | 'support' | 'finance';
export const ADMIN_ROLES: UserRole[] = ['master_admin', 'admin', 'support', 'finance'];

export type BlockedReason = 'blocked' | 'inactive' | null;

export interface UserProfile {
  id: string;
  name: string;
  full_name: string;
  display_name?: string;
  first_name?: string;
  last_name?: string;
  phone: string;
  cpf?: string;
  currency: string;
  timezone: string;
  goal: string;
  plan: 'free' | 'paid';
  has_bank_account: boolean;
  has_credit_card: boolean;
  wizard_completed: boolean;
  wizard_step: number;
  avatar_url?: string | null;
  auth_provider?: string;
  pin_enabled?: boolean;
  pin_hash?: string | null;
  pin_lock_timeout_minutes?: number;
  account_status?: string;
  is_active?: boolean;
  is_blocked?: boolean;
  last_seen_at?: string;
  created_at?: string;
  email?: string;
  // Address
  cep?: string;
  street?: string;
  street_number?: string;
  complement?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  country?: string;
  // Marketing / CRM
  lead_source?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  rdstation_contact_id?: string;
  mailchimp_subscriber_id?: string;
  marketing_opt_in?: boolean;
  internal_tags?: string[];
  internal_notes?: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  role: UserRole | null;
  modulePermissions: Record<string, boolean> | null;
  loading: boolean;
  roleLoading: boolean;
  blockedReason: BlockedReason;
  sessionExpired: boolean;
  clearSessionExpired: () => void;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  profile: null,
  role: null,
  modulePermissions: null,
  loading: true,
  roleLoading: true,
  blockedReason: null,
  sessionExpired: false,
  clearSessionExpired: () => {},
  signOut: async () => {},
  refreshProfile: async () => {},
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [role, setRole] = useState<UserRole | null>(null);
  const [modulePermissions, setModulePermissions] = useState<Record<string, boolean> | null>(null);
  // Ref to track whether we already have a resolved role (avoids resetting roleLoading on token refresh)
  const roleRef = useRef<UserRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [roleLoading, setRoleLoading] = useState(true);
  const [blockedReason, setBlockedReason] = useState<BlockedReason>(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  // sessionReadyRef: set to true once getSession() has fully resolved.
  const sessionReadyRef = useRef(false);

  const clearSessionExpired = useCallback(() => setSessionExpired(false), []);

  const fetchProfile = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      if (error) {
        console.warn('[Auth] fetchProfile error:', error.message);
        return;
      }
      if (data) {
        const p = data as UserProfile;
        // Block at app level: blocked accounts
        if (p.account_status === 'blocked') {
          console.warn('[Auth] account blocked — signing out');
          setBlockedReason('blocked');
          await supabase.auth.signOut();
          setProfile(null);
          setUser(null);
          setSession(null);
          setRole(null);
          return;
        }
        // Block at app level: inactive accounts
        if (p.account_status === 'inactive' || p.is_active === false) {
          console.warn('[Auth] account inactive — signing out');
          setBlockedReason('inactive');
          await supabase.auth.signOut();
          setProfile(null);
          setUser(null);
          setSession(null);
          setRole(null);
          return;
        }
        // Account is valid
        setBlockedReason(null);
        setProfile(p);
      }
    } catch (err) {
      console.warn('[Auth] fetchProfile exception:', err);
    }
  }, []);

  // ─── CRITICAL: uses get_my_role() RPC (SECURITY DEFINER) to bypass RLS ───
  const fetchRole = useCallback(async (userId: string, silent = false) => {
    // silent=true → skip loading spinner (used on tab refocus when role is already known)
    if (!silent) setRoleLoading(true);
    try {
      console.log('[Auth] fetchRole — userId:', userId, silent ? '(silent)' : '');
      const [{ data: roleData, error: roleError }, { data: permData }] = await Promise.all([
        supabase.rpc('get_my_role'),
        supabase
          .from('user_roles')
          .select('module_permissions')
          .eq('user_id', userId)
          .maybeSingle()
          .then((res) => res)
          .catch(() => ({ data: null })),
      ]);
      console.log('[Auth] fetchRole — RPC data:', roleData, '| error:', roleError?.message ?? null);

      if (roleError) {
        console.error('[Auth] fetchRole RPC error:', roleError.message);
        if (!silent) {
          setRole(null);
          roleRef.current = null;
        }
        return;
      }

      const resolved = (roleData as UserRole) ?? 'customer';
      console.log('[Auth] fetchRole — role resolved:', resolved);
      roleRef.current = resolved;
      setRole(resolved);

      // Module permissions (may be null if not set — means use role defaults)
      const mp = (permData as any)?.module_permissions ?? null;
      setModulePermissions(mp);
    } catch (err) {
      console.error('[Auth] fetchRole exception:', err);
      if (!silent) {
        setRole(null);
        roleRef.current = null;
      }
    } finally {
      if (!silent) setRoleLoading(false);
    }
  }, []);

  const ensureProfile = useCallback(async (authUser: User) => {
    try {
      const { data: existing } = await supabase
        .from('profiles')
        .select('id, auth_provider, avatar_url, email')
        .eq('id', authUser.id)
        .maybeSingle();

      const meta = authUser.user_metadata ?? {};
      const provider = authUser.app_metadata?.provider ?? 'email';

      if (!existing) {
        // Check if this user has an admin role — admins don't need the wizard
        const { data: roleData } = await supabase.rpc('get_my_role').maybeSingle().catch(() => ({ data: null }));
        const isAdminAccount = roleData && ['master_admin', 'admin', 'support', 'finance'].includes(roleData as string);

        await supabase.from('profiles').insert({
          id: authUser.id,
          email: authUser.email,
          name: meta.name ?? meta.given_name ?? authUser.email?.split('@')[0] ?? 'Usuário',
          full_name: meta.full_name ?? meta.name ?? '',
          display_name: meta.name ?? '',
          avatar_url: meta.avatar_url ?? meta.picture ?? null,
          auth_provider: provider,
          currency: 'BRL',
          timezone: 'America/Sao_Paulo',
          plan: 'free',
          account_status: 'active',
          wizard_completed: isAdminAccount ? true : false,
          wizard_step: isAdminAccount ? 6 : 1,
          pin_enabled: false,
          pin_lock_timeout_minutes: 15,
        });
      } else {
        const updates: Record<string, unknown> = {
          last_seen_at: new Date().toISOString(),
          auth_provider: provider,
        };
        if (authUser.email && !existing.email) {
          updates.email = authUser.email;
        }
        if (provider === 'google') {
          const googleAvatar = meta.avatar_url ?? meta.picture ?? null;
          if (googleAvatar && !existing.avatar_url) {
            updates.avatar_url = googleAvatar;
          }
        }
        // NOTE: never update wizard_completed, plan, account_status, or role here.
        await supabase.from('profiles').update(updates).eq('id', authUser.id);
      }
    } catch (err) {
      console.warn('[Auth] ensureProfile exception:', err);
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!user) return;
    await fetchProfile(user.id);
  }, [user, fetchProfile]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      console.warn('[Auth] timeout — forcing loading=false, roleLoading=false');
      setLoading(false);
      setRoleLoading(false);
    }, 8000);

    supabase.auth.getSession().then(({ data: { session: s }, error }) => {
      sessionReadyRef.current = true;

      // Handle invalid/expired refresh token gracefully
      if (error) {
        console.warn('[Auth] getSession error (token inválido):', error.message);
        // Clear invalid tokens from storage
        clearSupabaseAuthTokens();
        supabase.auth.signOut().catch(() => {});
        setSession(null);
        setUser(null);
        setRole(null);
        setRoleLoading(false);
        setLoading(false);
        // Show session expired banner so user knows to log in again
        if (error.message?.toLowerCase().includes('refresh token') || error.message?.toLowerCase().includes('invalid')) {
          setSessionExpired(true);
        }
        clearTimeout(timeout);
        return;
      }

      setSession(s);
      setUser(s?.user ?? null);

      console.log('[Auth] getSession — user:', s?.user?.email ?? 'none');

      if (s?.user) {
        // Run fetchRole and ensureProfile+fetchProfile in PARALLEL.
        // fetchRole does NOT depend on ensureProfile.
        Promise.all([
          fetchRole(s.user.id),
          ensureProfile(s.user).then(() => fetchProfile(s.user.id)),
        ]).finally(() => {
          setLoading(false);
          clearTimeout(timeout);
        });
      } else {
        setRole(null);
        setRoleLoading(false);
        setLoading(false);
        clearTimeout(timeout);
      }
    }).catch((err) => {
      console.error('[Auth] getSession error:', err);
      sessionReadyRef.current = true;
      // Also clear invalid session on catch
      clearSupabaseAuthTokens();
      supabase.auth.signOut().catch(() => {});
      setSession(null);
      setUser(null);
      setLoading(false);
      setRoleLoading(false);
      clearTimeout(timeout);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      console.log('[Auth] onAuthStateChange — event:', event, '| user:', s?.user?.email ?? 'none');

      if (event === 'TOKEN_REFRESHED') {
        setSession(s);
        return;
      }

      // Handle token refresh failure (network error, expired refresh token, etc.)
      if ((event as string) === 'TOKEN_REFRESH_FAILED') {
        console.warn('[Auth] TOKEN_REFRESH_FAILED — clearing session gracefully');
        clearSupabaseAuthTokens();
        supabase.auth.signOut().catch(() => {});
        setSession(null);
        setUser(null);
        setProfile(null);
        setRole(null);
        roleRef.current = null;
        setRoleLoading(false);
        setLoading(false);
        setSessionExpired(true);
        return;
      }

      setSession(s);
      setUser(s?.user ?? null);

      if (event === 'SIGNED_OUT') {
        setProfile(null);
        setRole(null);
        roleRef.current = null;
        setRoleLoading(false);
        return;
      }

      if (s?.user && (event === 'SIGNED_IN' || event === 'USER_UPDATED')) {
        if (!sessionReadyRef.current) {
          console.log('[Auth] SIGNED_IN skipped — getSession not ready yet');
          return;
        }

        const alreadyHasRole = roleRef.current !== null;

        if (alreadyHasRole) {
          // ── Silent refresh: role already known, just silently re-validate
          // and update last_seen_at in background. No loading spinners. ──
          console.log('[Auth] SIGNED_IN (tab refocus) — silent refresh, role:', roleRef.current);
          fetchRole(s.user.id, true);
          // Only update last_seen_at silently — do NOT call fetchProfile to avoid re-renders
          supabase
            .from('profiles')
            .update({ last_seen_at: new Date().toISOString() })
            .eq('id', s.user.id)
            .then(() => {})
            .catch(() => {});
          return;
        }

        console.log('[Auth] SIGNED_IN — first load, fetching role for:', s.user.email);

        // ── Mark as fresh login so PIN lock skips initial lock ────────────
        // This flag is read (and consumed) once by usePinLock on profile load.
        // sessionStorage is used so it's scoped to this browser tab/session.
        markFreshLogin();

        // Log the real login event (only fires on actual new logins, not tab refocuses)
        logUserEvent(s.user.id, 'login', {
          provider: s.user.app_metadata?.provider ?? 'email',
          email: s.user.email,
        });
        trackEvent('login', { method: s.user.app_metadata?.provider ?? 'email' });
        setRoleLoading(true);
        fetchRole(s.user.id);
        ensureProfile(s.user).then(() => fetchProfile(s.user.id));
      }
    });

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, [fetchProfile, fetchRole, ensureProfile]);

  const signOut = async () => {
    // Log logout before clearing session so RLS still allows the insert
    if (user?.id) {
      await logUserEvent(user.id, 'logout', { method: 'manual' });
    }
    trackEvent('logout');
    try {
      await supabase.auth.signOut();
    } catch (err) {
      console.warn('[Auth] signOut error (ignored — clearing local state anyway):', err);
    } finally {
      // Always clear local state even if the Supabase call fails
      setUser(null);
      setSession(null);
      setProfile(null);
      setRole(null);
      roleRef.current = null;
      setModulePermissions(null);
      setBlockedReason(null);
      setRoleLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, profile, role, modulePermissions, loading, roleLoading, blockedReason, sessionExpired, clearSessionExpired, signOut, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
